/**
 * Draft state and validation for the "Copy Procedure Template" dialog
 * (z8nrz7cz21).
 *
 * The id-remapping half of a copy lives on the SERVER
 * (cgt-backend `form-engine/copy-definition.ts`), because it must happen inside
 * the same transaction that writes the forms — a half-built copy leaves forms
 * with no template to reach them by. What is left for the client is naming and
 * the checks that can be made before anything is written, which is what this
 * module is.
 *
 * Pure, and in `src/lib/` rather than inline in the dialog, because
 * `vitest.config.ts` is `environment: node` with `include: ['src/**\/*.test.ts']`
 * — logic expressed inside a `.tsx` could never be tested.
 */

import type { ProcedureTemplateDetail, ProcedureTemplateSequenceStep } from '@/types/cgt'

/** One form of the source sequence, with the name its copy will take. */
export interface CopyFormDraft {
  formDefinitionId: string
  /** The source form's name, shown as the read-only left column. */
  sourceName: string
  /** What the user typed for the copy. */
  name: string
}

export interface CopyDraft {
  /** The source template's name, shown as the read-only left column. */
  sourceName: string
  /** What the user typed for the new template. */
  name: string
  forms: CopyFormDraft[]
}

/** The dialog's pre-filled default for any copied artifact. */
export function defaultCopyName(sourceName: string): string {
  return `Copy of ${sourceName}`
}

/**
 * The initial draft for a template version.
 *
 * Steps are deduplicated by `formDefinitionId`: the backend's
 * `@@unique([procedureTemplateId, formDefinitionId])` already means a form
 * appears at most once, but a legacy row that slipped past it would otherwise
 * render two inputs writing to one form and let the second silently win.
 */
export function buildCopyDraft(source: {
  name: string
  steps: ProcedureTemplateSequenceStep[]
}): CopyDraft {
  const seen = new Set<string>()
  const forms: CopyFormDraft[] = []
  for (const step of source.steps) {
    if (seen.has(step.formDefinitionId)) continue
    seen.add(step.formDefinitionId)
    forms.push({
      formDefinitionId: step.formDefinitionId,
      sourceName: step.formName,
      name: defaultCopyName(step.formName),
    })
  }
  return { sourceName: source.name, name: defaultCopyName(source.name), forms }
}

/** Draft for a full detail response — the shape `procedureTemplatesApi.get` returns. */
export function buildCopyDraftFromDetail(detail: ProcedureTemplateDetail): CopyDraft {
  return buildCopyDraft({ name: detail.name, steps: detail.steps ?? [] })
}

/** Replaces one form's name, leaving the rest of the draft alone. */
export function setFormName(draft: CopyDraft, formDefinitionId: string, name: string): CopyDraft {
  return {
    ...draft,
    forms: draft.forms.map(f => (f.formDefinitionId === formDefinitionId ? { ...f, name } : f)),
  }
}

/**
 * Per-field validation messages, keyed by `'template'` or a form definition id.
 * Empty means the draft can be submitted.
 *
 * Keyed rather than a single string so the dialog can put each message under
 * the input that caused it — with one shared error line, a user renaming four
 * forms cannot tell which row is the problem.
 */
export function validateCopyDraft(draft: CopyDraft): Record<string, string> {
  const errors: Record<string, string> = {}

  const name = draft.name.trim()
  if (!name) {
    errors.template = 'Give the copy a name.'
  } else if (name === draft.sourceName.trim()) {
    errors.template = 'Give the copy a name different from the source template.'
  }

  for (const form of draft.forms) {
    if (!form.name.trim()) {
      errors[form.formDefinitionId] = 'Give the copy a name.'
    }
  }

  return errors
}

export function isCopyDraftValid(draft: CopyDraft): boolean {
  return Object.keys(validateCopyDraft(draft)).length === 0
}

/**
 * The `POST /procedure-templates/:id/copy` body.
 *
 * Every form is named explicitly even when the user left the default in place:
 * the server's own fallback is also "Copy of <name>", but sending what the
 * dialog showed means the created copy always matches what the user was looking
 * at, rather than depending on the two defaults staying in step.
 */
export function buildCopyPayload(draft: CopyDraft): {
  name: string
  formNames: Array<{ formDefinitionId: string; name: string }>
} {
  return {
    name: draft.name.trim(),
    formNames: draft.forms.map(f => ({
      formDefinitionId: f.formDefinitionId,
      name: f.name.trim(),
    })),
  }
}

/**
 * Maps a server error onto the input that caused it, so a name collision lands
 * on the offending row instead of as one generic dialog error.
 *
 * The match is by name because that is what the server's messages quote (it
 * reports handle and name clashes by the form's name, having no notion of which
 * input box the user typed into). A message naming no row falls through to the
 * dialog-level error, which is the right place for everything else.
 */
export function mapCopyError(message: string, draft: CopyDraft): Record<string, string> {
  const quoted = [...message.matchAll(/"([^"]+)"/g)].map(m => m[1])
  if (quoted.length === 0) return {}

  const errors: Record<string, string> = {}
  for (const form of draft.forms) {
    if (quoted.includes(form.name.trim())) errors[form.formDefinitionId] = message
  }
  if (quoted.includes(draft.name.trim())) errors.template = message
  return errors
}
